import { renderElement } from "../../utils/use-render.js";
import { MenuTriggerChildContextProvider, useMenuContext } from "./context.js";
import { useSafeId } from "../../utils/use-safe-id.js";
import { callKeyDownHandler, preventMenuKeyDefault } from "./keyboard.js";
import { isMenuNavigationKey } from "@videojs/core/dom";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { isInteractiveActivation } from "@videojs/utils/dom";
import { jsx } from "react/jsx-runtime";
//#region src/ui/menu/trigger.tsx
/**
* Button that toggles the menu visibility. At root level renders a `<button>`. When inside a parent menu (as a submenu
* trigger), renders as a `<div role="menuitem">` that opens the submenu on click or ArrowRight.
*/
const MenuTrigger = forwardRef(function MenuTrigger({ render, className, style, disabled, onClick, onKeyDown, ...elementProps }, forwardedRef) {
	const { core, menu, parent, state, contentId, optionState } = useMenuContext();
	const resolvedDisabled = disabled || optionState?.disabled || false;
	const isSubMenuTrigger = parent !== null;
	const controlledId = state.open || state.status === "ending" ? contentId : void 0;
	const elementRef = useRef(null);
	const triggerId = useSafeId("sub-trigger");
	const parentMenuApi = parent?.menu ?? null;
	useEffect(() => {
		if (!isSubMenuTrigger || !parentMenuApi) return;
		const element = elementRef.current;
		if (!element) return;
		menu.setTriggerElement(element);
		const unregister = parentMenuApi.registerItem(element);
		return () => {
			unregister();
			menu.setTriggerElement(null);
		};
	}, [
		isSubMenuTrigger,
		menu,
		parentMenuApi
	]);
	const openSubMenu = useCallback(() => {
		if (!resolvedDisabled) menu.open("click");
	}, [resolvedDisabled, menu]);
	const handleSubMenuClick = useCallback((event) => {
		onClick?.(event);
		if (event.defaultPrevented) return;
		openSubMenu();
	}, [onClick, openSubMenu]);
	const handleSubMenuKeyDown = useCallback((event) => {
		const defaultPreventedByUser = callKeyDownHandler(onKeyDown, event);
		if (resolvedDisabled || defaultPreventedByUser) return;
		if (event.key === "ArrowRight") {
			event.preventDefault();
			openSubMenu();
		}
	}, [
		resolvedDisabled,
		onKeyDown,
		openSubMenu
	]);
	const handlePointerEnter = useCallback(() => {
		const element = elementRef.current;
		if (!element || resolvedDisabled || !parentMenuApi) return;
		parentMenuApi.highlight(element, {
			focus: false,
			pointer: true
		});
	}, [resolvedDisabled, parentMenuApi]);
	const triggerRef = useCallback((element) => {
		menu.setTriggerElement(element);
	}, [menu]);
	const handleRootKeyDown = useCallback((event) => {
		const defaultPreventedByUser = callKeyDownHandler(onKeyDown, event);
		if (resolvedDisabled || defaultPreventedByUser) {
			if (resolvedDisabled && isMenuNavigationKey(event)) event.preventDefault();
			return;
		}
		if (isInteractiveActivation(event.nativeEvent)) {
			event.preventDefault();
			menu.triggerProps.onClick(event.nativeEvent);
			return;
		}
		menu.triggerProps.onKeyDown(event);
	}, [
		resolvedDisabled,
		menu.triggerProps,
		onKeyDown
	]);
	const rootTriggerProps = useMemo(() => ({
		onClick: resolvedDisabled ? (event) => event.preventDefault() : menu.triggerProps.onClick,
		onKeyDown: handleRootKeyDown
	}), [
		resolvedDisabled,
		handleRootKeyDown,
		menu.triggerProps
	]);
	if (isSubMenuTrigger) return /* @__PURE__ */ jsx(MenuTriggerSubmenu, {
		render,
		className,
		style,
		disabled: resolvedDisabled,
		elementProps,
		forwardedRef,
		elementRef,
		triggerId,
		triggerAttrs: core.getTriggerAttrs(state, controlledId),
		availability: optionState?.availability,
		hidden: optionState?.hidden,
		state,
		onSubMenuClick: handleSubMenuClick,
		onSubMenuKeyDown: handleSubMenuKeyDown,
		onPointerEnter: handlePointerEnter
	});
	return /* @__PURE__ */ jsx(MenuTriggerChildContextProvider, {
		value: true,
		children: renderElement("button", {
			render,
			className,
			style
		}, {
			state,
			ref: [forwardedRef, triggerRef],
			props: [
				{
					type: "button",
					...core.getTriggerAttrs(state, controlledId)
				},
				resolvedDisabled ? {
					disabled: true,
					"aria-disabled": "true"
				} : void 0,
				optionState ? { "data-availability": optionState.availability } : void 0,
				state.open ? { onKeyDownCapture: preventMenuKeyDefault } : void 0,
				rootTriggerProps,
				elementProps,
				optionState?.hidden ? { hidden: true } : void 0
			]
		})
	});
});
function MenuTriggerSubmenu({ render, className, style, disabled, elementProps, forwardedRef, elementRef, triggerId, triggerAttrs, availability, hidden, state, onSubMenuClick, onSubMenuKeyDown, onPointerEnter }) {
	return renderElement("div", {
		render,
		className,
		style
	}, {
		state,
		ref: [forwardedRef, elementRef],
		props: [
			{
				id: triggerId,
				role: "menuitem",
				...triggerAttrs,
				"aria-disabled": disabled ? true : void 0,
				"data-availability": availability,
				"data-has-submenu": "",
				onClick: onSubMenuClick,
				onKeyDownCapture: preventMenuKeyDefault,
				onKeyDown: onSubMenuKeyDown,
				onPointerEnter
			},
			elementProps,
			hidden ? { hidden: true } : void 0
		]
	});
}
//#endregion
export { MenuTrigger };

//# sourceMappingURL=trigger.js.map